import React, { useMemo, useState } from "react";
import EmailVerificationModal from "./EmailVerificationModal.jsx";
import { renderAgencyVerificationPDF } from "./renderAgencyVerificationPDF.js";

const OfficeCard = props => {
  const {
    details = {},
    detailsLink = "#",
    showBadgeText = false,
    isAgencyVerification = false,
    underwriterCode = "",
    serviceState = "",
    addressLabel,
    underwriterLabel,
    serviceStateLabel,
    officeType,
    phoneLabel,
    faxLabel,
    mapLabel,
    detailsLabel,
    agentType,
  } = props;

  const { title, mailingAddress, phone, fax, distance } = details;

  // Determine if Stewart owned based on agentType: 'I' = Independent, anything else = Stewart
  const stewartOwned = agentType !== "I";

  const brandClass = stewartOwned
    ? "is-stewart"
    : isAgencyVerification
      ? "is-agency"
      : "is-independent";
  const badgeText = stewartOwned
    ? officeType?.find(t => t.key === "stewart")?.label || "Stewart Office"
    : officeType?.find(t => t.key === "agency")?.label || "Independent Agency";
  const badgeIconClass = stewartOwned ? "fal fa-building" : "far fa-male";
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const formatAddress = (details = {}) => {
    const a = details.mailingAddress || {};
    const street = [a.street, a.street2].filter(Boolean).join(" ");
    const citySt = [a.city, a.state].filter(Boolean).join(", ");
    return [street, citySt, a.zip]
      .filter(Boolean)
      .join(" ")
      .replace(/\s{2,}/g, " ")
      .trim();
  };

  const buildGoogleMapsUrl = (details = {}, mode = "search") => {
    const addr = formatAddress(details) || details.title || "";
    return `https://www.google.com/maps/${mode}/?api=1&query=${encodeURIComponent(
      addr,
    )}`;
  };

  const computedMapUrl = useMemo(() => {
    return buildGoogleMapsUrl(details, "search");
  }, [details]);

  const mapHref = computedMapUrl;

  const printVerificationLetter = async e => {
    e.preventDefault();
    renderAgencyVerificationPDF(props);
  };

  const formatPhoneNumber = phoneNumber => {
    if (!phoneNumber) return phoneNumber;
    const formatted = phoneNumber.replace(/\D/g, "");
    if (formatted.length === 10) {
      return `(${formatted.slice(0, 3)}) ${formatted.slice(3, 6)}-${formatted.slice(6)}`;
    }
    return phoneNumber;
  };

  return (
    <article className={`office-card ${brandClass}`}>
      {modalIsOpen && (
        <EmailVerificationModal
          office={props}
          closeModal={() => setModalIsOpen(false)}
        />
      )}
      {showBadgeText && !isAgencyVerification && (
        <div className="office-card__badge">
          <span className="office-card__badge-icon" aria-hidden="true">
            <i className={badgeIconClass} />
          </span>
          <span className="office-card__badge-text">{badgeText}</span>
        </div>
      )}

      <header className="office-card__header">
        <h3 className="office-card__title">{title}</h3>
      </header>

      <div className="office-card__content">
        <div className="office-card__address">
          {isAgencyVerification && (
            <p className="agency-verification__label">{addressLabel}</p>
          )}
          <span
            className={`${isAgencyVerification ? "agency-verification__street" : "office-card__street"}`}
          >
            {mailingAddress?.street}
          </span>
          {mailingAddress?.street2 && (
            <div
              className={`${isAgencyVerification ? "agency-verification__line2" : "office-card__line2"}`}
            >
              {mailingAddress?.street2}
            </div>
          )}
          <div
            className={`${isAgencyVerification ? "agency-verification__city" : "office-card__city"}`}
          >
            {mailingAddress?.city}, {mailingAddress?.state}{" "}
            {mailingAddress?.zip}
          </div>
        </div>
        {!isAgencyVerification && (
          <div className="office-card__contact">
            {phone && (
              <div className="office-card__row">
                {phoneLabel && (
                  <span className="office-card__label">{phoneLabel}</span>
                )}
                <span className="office-card__phone">
                  {formatPhoneNumber(phone)}
                </span>
              </div>
            )}
            {fax && (
              <div className="office-card__row">
                <span className="office-card__label">{faxLabel}</span>
                <span className="office-card__fax">
                  {formatPhoneNumber(fax)}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
      {!isAgencyVerification && (
        <footer className="office-card__footer">
          <div className="office-card__action-container">
            <div className="office-card__action">
              <i
                className="fal fa-map-marker-alt office-card__fa"
                aria-hidden="true"
              />
              <a
                className="office-card__action office-card__action--map"
                href={mapHref}
                target="_blank"
                rel="noreferrer"
              >
                <span>{mapLabel}</span>
              </a>
            </div>

            {detailsLink && (
              <a
                className="office-card__action"
                href={detailsLink}
                target="_blank"
                rel="noreferrer"
              >
                {detailsLabel}
              </a>
            )}
          </div>
          {distance && (
            <span className="office-card__distance">{`${distance} Miles`}</span>
          )}
        </footer>
      )}
      {/* Agency Verification */}
      {isAgencyVerification && (
        <footer className="agency-verification__footer">
          <div className="agency-verification__info">
            <span>
              <strong>{underwriterLabel}:</strong> {underwriterCode}
            </span>
            <span>
              <strong>{serviceStateLabel}:</strong> {serviceState}
            </span>
          </div>
          <div className="agency-verification__action">
            <span>
              <button onClick={e => printVerificationLetter(e)}>
                <i className="fal fa-print" aria-hidden="true" /> Print
                Verification Letter
              </button>
            </span>
            <span>
              <button onClick={() => setModalIsOpen(true)}>
                <i className="fal fa-envelope" aria-hidden="true" /> Email
                Verification Letter
              </button>
            </span>
          </div>
        </footer>
      )}
    </article>
  );
};

export default OfficeCard;
